import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {BaseChartConfig} from '@core-app/blocks/block-compensation-history/chart';
import type {ChartConfiguration, ChartItem} from 'chart.js';
import {BarController, BarElement, CategoryScale, Chart, Colors, Legend, LinearScale, Tooltip,} from 'chart.js';
import {merge} from 'lodash';
import {BlockStatementAbstractComponent} from '../block-statement-abstract.component';

@Component({
    selector: 'core-block-compensation-history',
    templateUrl: './block-compensation-history.component.html',
    standalone: false,
})
export class BlockCompensationHistoryComponent
    extends BlockStatementAbstractComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    hideFigures$ = this.trsService.getFiguresToggleState$();

    private compensationHistoryChartInstance?: Chart;
    private chartCanvas?: HTMLCanvasElement;
    private chartImageDiv?: HTMLDivElement;
    private chartMediaQuery?: MediaQueryList;

    override ngOnInit(): void {
        super.ngOnInit();

        this.chartMediaQuery = matchMedia('(min-width: 800px)');
        this.chartMediaQuery.addEventListener('change', (media) =>
            this.changeChartPresentation(!media.matches)
        );

        this.chartCanvas = document.getElementById('stackedBarChart') as HTMLCanvasElement;
        // Ensure ChartJS chart shows up properly in print
        this.prepareChartForPrint();
    }

    ngAfterViewInit() {
        const isMobile = !this.chartMediaQuery?.matches;
        this.changeChartPresentation(isMobile);
    }

    override ngOnDestroy() {
        if (this.compensationHistoryChartInstance) {
            this.compensationHistoryChartInstance.destroy();
        }

        if (this.chartMediaQuery) {
            this.chartMediaQuery.removeEventListener('change', (media) =>
                this.changeChartPresentation(!media.matches)
            );
        }
    }

    initializeCompensationHistoryBarChart(
        configOverrides: Partial<ChartConfiguration> = {}
    ) {
        Chart.register(
            Colors,
            BarController,
            CategoryScale,
            LinearScale,
            BarElement,
            Legend,
            Tooltip
        );

        // This prevents the "Error: Canvas is already in use.
        // Chart with ID '0' must be destroyed before the canvas
        // with ID 'stackedBarChart' can be reused." error
        this.compensationHistoryChartInstance?.destroy();

        const compensationHistoryChartLabels: number[] = [];
        const compensationHistoryChartDataAP: number[] = [];
        const compensationHistoryChartDataAIP: number[] = [];
        const compensationHistoryChartDataPerDiem: number[] = [];

        const startYear = 2022;
        const currentYear = new Date().getFullYear();
        for (let year = startYear; year <= currentYear; year++) {
            const ap = this.authUser.data[`${year}_AP`];
            const aip = this.authUser.data[`${year}_AIP`];
            const perDiem = this.authUser.data[`${year}_PER_DIEM`];

            // Only continue if at least one value is non-null and non-zero
            if (!ap && !aip && !perDiem) continue;

            compensationHistoryChartLabels.push(year);
            compensationHistoryChartDataAP.push(ap || 0);
            compensationHistoryChartDataAIP.push(aip || 0);
            compensationHistoryChartDataPerDiem.push(perDiem || 0);
        }

        const datasets = [];
        if (
            compensationHistoryChartDataAP.some(
                (val) => val !== 0 && val != null
            )
        ) {
            datasets.push({
                label: 'Annual Pay',
                data: compensationHistoryChartDataAP,
                backgroundColor: '#007bc7',
                stack: 'stack1',
            });
        }

        if (
            compensationHistoryChartDataAIP.some(
                (val) => val !== 0 && val != null
            )
        ) {
            datasets.push({
                label: `Supplemental Compensation*`,
                data: compensationHistoryChartDataAIP,
                backgroundColor: '#ffcfb9',
                stack: 'stack1',
            });
        }

        if (
            compensationHistoryChartDataPerDiem.some(
                (val) => val !== 0 && val != null
            )
        ) {
            datasets.push({
                label: 'Annual Per Diem',
                data: compensationHistoryChartDataPerDiem,
                backgroundColor: '#d3ff79',
                stack: 'stack1',
            });
        }

        const combinedMaxValues = Array.from(
            { length: compensationHistoryChartDataAP.length },
            (_, index) =>
                Number(compensationHistoryChartDataAP[index] ?? 0) +
                Number(compensationHistoryChartDataAIP[index] ?? 0) +
                Number(compensationHistoryChartDataPerDiem[index] ?? 0)
        );

        (configOverrides as any).options.scales.y.max =
            Math.ceil((Math.max(...combinedMaxValues) ?? 200000) / 50000) *
            50000;

        this.chartCanvas!.style.backgroundColor = '#f5f5f5';

        const data = {
            data: {
                labels: compensationHistoryChartLabels,
                datasets,
            },
        };
        const config = merge({}, BaseChartConfig, configOverrides, data);
        this.compensationHistoryChartInstance = new Chart(this.chartCanvas as ChartItem, config);
    }

    changeChartPresentation(isMobile: boolean) {
        const chartPadding = isMobile ? 24 : 48;
        const aspectRatio = isMobile ? 0.5 : 2;
        const legendPadding = isMobile ? 24 : 45;
        const legendLabelFontSize = isMobile ? 14 : 15;
        const legendAxisFontSize = isMobile ? 14 : 16;

        this.initializeCompensationHistoryBarChart({
            options: {
                aspectRatio,
                plugins: {
                    legend: {
                        labels: {
                            font: { size: legendLabelFontSize },
                            padding: legendPadding,
                        },
                    },
                },
                layout: {
                    padding: chartPadding,
                },
                scales: {
                    x: {
                        ticks: { font: { size: legendAxisFontSize } },
                    },
                    y: {
                        ticks: { font: { size: legendAxisFontSize } },
                    },
                },
            },
        });
    }

    prepareChartForPrint() {
        window.addEventListener('beforeprint', () => {
            if (!this.chartCanvas) return;

            if (this.chartImageDiv) {
                const chartWrapper = document.querySelector('.chart-wrapper');
                chartWrapper?.insertBefore(
                    this.chartImageDiv,
                    chartWrapper.firstChild
                );
                this.chartImageDiv.style.display = 'block';
            } else {
                // Convert canvas to image
                const div = document.createElement('div');
                div.id = 'chart-image-for-print';

                const chartImage = new Image();
                chartImage.src = this.chartCanvas.toDataURL('image/png');
                chartImage.id = 'stackedBarChartImage';
                chartImage.style.width = '100%';
                chartImage.style.height = '100%';

                // Hide original canvas for print to prevent layout shifts
                this.chartCanvas.style.display = 'none';

                // Hide original footnote so it doesn't show
                // as a duplicate
                const footnote = document.querySelector('.chart-wrapper__footnote') as HTMLElement;
                footnote.style.opacity = '0';

                // Create a destroyable clone of footnote
                const footnoteClone = footnote.cloneNode(true) as HTMLElement;
                footnoteClone.style.opacity = '1';

                div.appendChild(chartImage);
                div.appendChild(footnoteClone);

                // Save constructed <div> to prevent re-generating
                // a new canvas image
                this.chartImageDiv = div;

                // Insert image adjacent to now hidden canvas
                const chartWrapper = document.querySelector('.chart-wrapper');
                chartWrapper?.insertBefore(
                    this.chartImageDiv,
                    chartWrapper.firstChild
                );
            }
        });

        window.addEventListener('afterprint', () => {
            document.getElementById('chart-image-for-print')?.remove();

            // Hide generated chart image and show original ChartJS chart
            this.chartImageDiv!.style.display = 'none';
            this.chartCanvas!.style.display = 'block';

            // Show original footnote again
            const footnote = document.querySelector('.chart-wrapper__footnote') as HTMLElement;
            footnote.style.opacity = '1';
        });
    }
}
